import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Tabs, Tab, Typography, Alert } from '@mui/material';
import Header from '../components/Header';
import BlogForm from '../components/BlogForm';
import BlogList from '../components/BlogList';
import Loader from '../components/Loader';
import { getMe, getWebsites } from '../api/admin';
import { color, font } from '../styles/tokens';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const history = useHistory();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      history.push('/Enteria');
      return;
    }
    Promise.all([getMe(token), getWebsites(token)])
      .then(([meRes, websitesRes]) => {
        setUser(meRes.data);
        setWebsites(websitesRes.data);
      })
      .catch(err => {
        if (err?.response?.status === 401 || err?.response?.status === 400) {
          localStorage.removeItem('token');
          history.push('/Enteria');
          return;
        }
        setLoadError('Couldn’t load your dashboard. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [token, history]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    history.push('/Enteria');
  };

  if (loading) return <Loader minHeight="100vh" />;

  if (loadError) {
    return (
      <Box sx={{ minHeight: '100vh', background: color.ink, p: 4 }}>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  // Non-superadmins can only file posts against properties a superadmin
  // has assigned them — mirrors the check the API enforces server-side.
  const assignedWebsiteIds = new Set((user.websites || []).map(w => w._id || w));
  const postableWebsites = user.role === 'superadmin'
    ? websites
    : websites.filter(w => assignedWebsiteIds.has(w._id));

  return (
    <Box sx={{ minHeight: '100vh', background: color.ink }}>
      <Header user={user} onLogout={handleLogout} websiteCount={websites.length} />
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: 4 }}>
        <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', letterSpacing: '.14em', textTransform: 'uppercase', color: color.boneDim, mb: 0.5 }}>
          {user.role === 'superadmin' ? 'Superadmin' : 'Admin'} desk
        </Typography>
        <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '1.5rem', color: color.bone, mb: 3 }}>
          The Desk
        </Typography>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3, borderBottom: `1px solid ${color.ruleSoft}` }}
        >
          <Tab label="My dispatches" />
          <Tab label="Compose" />
        </Tabs>
        {tab === 0 && <BlogList user={user} websites={websites} />}
        {tab === 1 && <BlogForm websites={postableWebsites} user={user} />}
      </Box>
    </Box>
  );
};

export default Dashboard;
